/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useMemo } from 'react';
import { BuildingType, CityStats, Grid } from '../../types';
import { computeBuildPreview } from '../../utils/buildPreview';
import { surfaces, type as typeTokens, infoCards, cn } from './kit';

export type BuildPreviewPanelProps = {
  grid: Grid;
  stats: CityStats;
  selectedTool: BuildingType;
  hoveredTile: { x: number; y: number } | null;
  visible: boolean;
};

function formatDelta(value: number, suffix = ''): string {
  if (value === 0) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}${suffix}`;
}

const BuildPreviewPanel: React.FC<BuildPreviewPanelProps> = ({
  grid,
  stats,
  selectedTool,
  hoveredTile,
  visible,
}) => {
  const preview = useMemo(() => {
    if (!visible || !hoveredTile || selectedTool === BuildingType.None) return null;
    return computeBuildPreview(grid, hoveredTile.x, hoveredTile.y, selectedTool, stats.budget);
  }, [visible, hoveredTile, selectedTool, grid, stats.budget]);

  if (!preview) return null;

  const budgetAfter = stats.budget + preview.budgetDelta;

  return (
    <div
      data-testid="build-preview-panel"
      className="pointer-events-none absolute bottom-28 md:bottom-32 left-1/2 -translate-x-1/2 z-40 w-[min(22rem,calc(100vw-2rem))]"
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          surfaces.panel,
          'px-3 py-2 md:px-4 md:py-2.5',
          preview.isValid
            ? cn(infoCards.enables, 'border-cyan-500/40 shadow-[0_0_20px_rgba(34,211,238,0.15)]')
            : 'border-red-500/40 shadow-[0_0_20px_rgba(239,68,68,0.15)] bg-red-950/20',
        )}
      >
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="min-w-0">
            <span className={infoCards.enablesLabel}>Place</span>
            <h3 className={cn(typeTokens.title, 'text-sm truncate')}>{preview.buildingName}</h3>
          </div>
          <div className="text-right shrink-0">
            <span className={infoCards.valueLabel}>Cost</span>
            <div className={cn(typeTokens.mono, 'font-bold text-sm', preview.isValid ? 'text-emerald-400' : 'text-red-400')}>
              ${preview.cost.toLocaleString()}
            </div>
          </div>
        </div>

        <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] md:text-xs border-t border-slate-700/60 pt-1.5', typeTokens.mono)}>
          <span className={typeTokens.eyebrow}>Per tick</span>
          <span
            className={
              preview.budgetGenPerTick > 0
                ? 'text-green-300'
                : preview.budgetGenPerTick < 0
                  ? 'text-red-300'
                  : 'text-slate-400'
            }
          >
            💰 {formatDelta(preview.budgetGenPerTick)}/t
          </span>
          <span
            className={
              preview.complianceDelta > 0
                ? 'text-cyan-300'
                : preview.complianceDelta < 0
                  ? 'text-orange-300'
                  : 'text-slate-400'
            }
          >
            📊 {formatDelta(preview.complianceDelta, '%')}/t
          </span>
          <span
            className={
              preview.threatDelta > 0
                ? 'text-red-300'
                : preview.threatDelta < 0
                  ? 'text-emerald-300'
                  : 'text-slate-400'
            }
          >
            ⚠ {formatDelta(preview.threatDelta)}/t
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mt-1.5 text-[10px]">
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide',
              preview.willConnect ? infoCards.enables : infoCards.value,
            )}
          >
            {preview.willConnect ? '● Will connect' : '○ Isolated — needs pipeline'}
          </span>
          {preview.isValid && (
            <span className={cn(typeTokens.mono, typeTokens.caption)}>
              After: <span className="text-slate-300">${budgetAfter.toLocaleString()}</span>
            </span>
          )}
        </div>

        {preview.errors.length > 0 && (
          <ul className="mt-1.5 text-[10px] text-red-300 space-y-0.5 border-t border-red-900/30 pt-1.5">
            {preview.errors.map((err) => (
              <li key={err} className="flex items-start gap-1">
                <span aria-hidden>✗</span>
                <span>{err}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BuildPreviewPanel;